import type { TFunction } from 'i18next';
import type { IDeliverySourceBooked } from '@interfaces/ILogistics';

/** How many element types and floor names the Booked row spells out before
 *  the rest become a count — the board card's own "two, then + N more" rule
 *  (`FACE_LINES`), so the drawer and the card behave alike (Niv, 2026-09-15). */
export const BOOKED_SHOWN = 2;

export interface IBookedSummary {
  /** "Booked · 14 × Slab · 6 × Wall" — the label and the first types. */
  kinds: string;
  /** "+3 types", or '' when every type is spelled out. */
  moreKinds: string;
  /** "3F, 4F", or "5 floors" past the limit; '' when no floor is known. */
  floors: string;
  /** The floors were counted rather than named. */
  floorsCounted: boolean;
  /** Everything, for the hint tooltip — null when nothing was cut. */
  full: { title: string; lines: string[] } | null;
}

/**
 * What a "Raised from task" card's Booked row says about a task's booking:
 * the first two element types in full and the rest as a count, the floors
 * named up to two and counted past that, so the row stays one line however
 * much the task booked. A short list reads exactly as it always did.
 *
 * Floor ids the site catalog does not hold drop out — the same as before.
 */
export function bookedSummary(
  booked: IDeliverySourceBooked,
  floorNames: ReadonlyMap<string, string>,
  t: TFunction,
): IBookedSummary {
  const floors = booked.floorIds.flatMap((id) => {
    const name = floorNames.get(id);
    return name === undefined ? [] : [name];
  });
  const kindTexts = booked.elements.map((el) => t('logistics.source.bookedKind', { count: el.count, kind: el.kind }));
  const hiddenKinds = kindTexts.length - BOOKED_SHOWN;
  const floorsCounted = floors.length > BOOKED_SHOWN;
  const elements = booked.elements.reduce((sum, el) => sum + el.count, 0);
  const floorList = floors.join(t('common.listSeparator'));
  return {
    // The separator is structural punctuation, not copy — the card's meta
    // line uses the same literal " · ".
    kinds: [t('logistics.source.booked'), ...kindTexts.slice(0, BOOKED_SHOWN)].join(' · '),
    moreKinds: hiddenKinds > 0 ? t('logistics.source.moreKinds', { count: hiddenKinds }) : '',
    floors: floorsCounted ? t('logistics.source.floorCount', { count: floors.length }) : floorList,
    floorsCounted,
    full:
      hiddenKinds > 0 || floorsCounted
        ? {
            title: t('logistics.source.elementCount', { count: elements }),
            lines: [kindTexts.join(' · '), ...(floors.length > 0 ? [floorList] : [])],
          }
        : null,
  };
}
